import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CartDrawerView } from "./cartDrawerView";
import { useCartManager } from "./CartManager";
export const CartManagerCtx = React.createContext(null);
const CartManagerProvider = CartManagerCtx.Provider;
function CartDrawer() {
    const cartManager = useCartManager();
    const didMountRef = useRef(false);
    const [swiperRunning, setSwiperRunning] = useState(false);
    useEffect(() => {
        cartManager.getCart();
    }, []);
    // NOTE: Shopify.actions.updateCart/openCart are configured in
    // snippets/standard-cart-actions.liquid, rendered above
    // {{ content_for_header }} in every layout. It used to be done here, but a
    // React effect mounts too late and too conditionally — on a PDP it had
    // still not run 6s after load, leaving updateCart on its platform default,
    // which full-page-reloads and closed the drawer under the buyer. Only the
    // first configure() call wins, so there must be exactly one owner. Do not
    // reintroduce it here.
    useEffect(() => {
        if (didMountRef.current) {
            window._uxa = window._uxa || [];
            if (cartManager.cartOpen) {
                window._uxa.push(["setQuery", "cart-drawer-open"]);
            }
            else {
                window._uxa.push(["setQuery", "cart-drawer-closed"]);
            }
            window._uxa.push(["trackPageview"]);
        }
        didMountRef.current = true;
    }, [cartManager.cartOpen]);
    const calculateMissingAmount = () => {
        let missingAmount = 0;
        //check if it has subscription and return 0 if so
        if (!!cartManager.items.find(item => item.selling_plan_allocation))
            return 0;
        cartManager.items.forEach((item) => {
            if (!item.product.tags.includes('no-free-shipping')) {
                missingAmount += item.quantity * (item.final_price / 100);
            }
        });
        return 50 - missingAmount;
    };
    const shippingWidgetInfo = {
        missingAmount: calculateMissingAmount(),
        amountNeeded: 50
    };
    const cartQuantity = cartManager.items.reduce((previous, item) => previous + item.quantity, 0);
    const resizeImage = (url, size) => {
        const splitUrl = url.split(".");
        const extension = splitUrl.pop();
        const urlWithoutExtension = splitUrl.join(".");
        let sizeString = "_";
        if (size.width)
            sizeString += size.width;
        if (size.width && !size.height)
            sizeString += "x";
        if (size.height)
            sizeString += "x" + size.height;
        return `${urlWithoutExtension}${sizeString}.${extension}`;
    };
    const onChangeSubscriptionFrequency = (item, optionIndex, cartItemIndex) => {
        window._uxa.push(["setQuery", "cart-drawer-add-subscription"]);
        window._uxa.push(["trackPageview"]);
        cartManager.changeItem(item, optionIndex, cartItemIndex);
    };
    const removeSubscriptionState = (item, cartItemIndex) => {
        window._uxa.push(["setQuery", "cart-drawer-remove-subscription"]);
        window._uxa.push(["trackPageview"]);
        cartManager.removeSubscription(item, cartItemIndex);
    };
    const addBundleToCart = (product) => {
        window._uxa.push([
            'ec:cart:add',
            {
                sku: product.variants[0].sku /* mandatory - Product code (SKU) (string) */,
            },
        ]);
        const items = {
            items: [
                {
                    quantity: 1,
                    id: product.variants[0].id.toString(),
                    properties: {},
                }
            ]
        };
        cartManager.addItem(items);
    };
    const cartTotal = cartManager.items.reduce((accumulator, value) => accumulator + (value.final_line_price / 100), 0);
    const totalSavings = cartManager.items.reduce((accumulator, value) => {
        let savings = 0;
        if (value.selling_plan_allocation) {
            savings = (value.selling_plan_allocation.compare_at_price - value.selling_plan_allocation.price) * value.quantity;
        }
        else if (value.compare_at_price && value.compare_at_price > value.price) {
            savings = (value.compare_at_price - value.price) * value.quantity;
        }
        return accumulator + (savings / 100);
    }, cartManager.totalDiscount);
    const scrollLock = () => {
        if (!(window.mobileMenuOpen)) {
            if (window.cartManager.cartOpen) {
                const body = document.getElementsByTagName('body')[0];
                body.style.overflow = 'hidden';
            }
            else {
                //get body and remove scroll lock
                const body = document.getElementsByTagName('body')[0];
                body.style.overflow = 'auto';
            }
        }
    };
    const applySwiper = () => {
        const waitForSwiper = setInterval(() => {
            const slideElement = document.querySelector('.swiper-cart-drawer-suggested-products');
            if (typeof window !== 'undefined' && window.Swiper !== undefined && slideElement) {
                clearInterval(waitForSwiper);
                const Swiper = window.Swiper;
                const swiper = new Swiper('.swiper-cart-drawer-suggested-products', {
                    slidesPerView: 'auto',
                    spaceBetween: 12,
                    freeMode: true,
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                });
                setSwiperRunning(true);
            }
        }, 500);
    };
    useEffect(() => {
        if (swiperRunning)
            return;
        applySwiper();
    }, []);
    useEffect(() => {
        window.addEventListener('resize', scrollLock);
        return () => {
            window.removeEventListener('resize', scrollLock);
        };
    }, []);
    useEffect(() => {
        scrollLock();
    }, [cartManager.cartOpen]);
    const recommendedIntervals = vitalData.recommendedIntervals || { variants: {} };
    const isDesignMode = window.isDesignMode && window.cartDrawerDesignModeVisible;
    const featuredBannerData = {
        colorScheme: window.cart_featured_banner_color_scheme,
        image: window.cart_featured_banner_image,
        enabled: window.cart_featured_banner_enabled,
        title: window.cart_featured_banner_title,
        titleFont: window.cart_featured_banner_title_font,
        CTAText: window.cart_featured_banner_cta_text,
        CTATextFont: window.cart_featured_banner_cta_text_font,
        CTAAction: window.cart_featured_banner_cta_action,
        product: window.cart_featured_banner_cta_product,
        link: window.cart_featured_banner_cta_link,
        backgroundSize: window.cart_featured_banner_background_size,
    };
    const getHashURL = window.location.hash.includes("#show_cart");
    useEffect(() => {
        if (getHashURL && !cartManager.isLoading) {
            cartManager.openCart();
        }
    }, [cartManager.isLoading]);
    return (React.createElement(CartManagerProvider, { value: cartManager },
        React.createElement(CartDrawerView, { resizeImage: resizeImage, addBundleToCart: addBundleToCart, cartTotal: cartTotal, totalSavings: totalSavings, removeSubscriptionState: removeSubscriptionState, onChangeSubscriptionFrequency: onChangeSubscriptionFrequency, cartQuantity: cartQuantity, closeCart: cartManager.closeCart, isOpen: isDesignMode || cartManager.cartOpen, shippingWidgetInfo: shippingWidgetInfo, recommendedIntervals: recommendedIntervals, termsURL: cartManager.termsURL, subscriptionDisclosure: cartManager.subscriptionDisclosure, featuredBannerData: featuredBannerData })));
}
const cartDrawerContainer = document.getElementById('cart-drawer-container');
if (cartDrawerContainer) {
    ReactDOM.render(React.createElement(CartDrawer, null), cartDrawerContainer);
}
